import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '@/api/supabaseClient';
import { auth } from '@/api/auth';

// Auth state for the whole app, backed by supabase.auth.onAuthStateChange.
//
// The Base44 version also fetched the platform's app public-settings endpoint
// and translated a 403 into 'auth_required' / 'user_not_registered'. Those
// concepts belonged to Base44's multi-tenant hosting, not to this app: there is
// no tenant to be unregistered for any more, so the whole path is gone along
// with UserNotRegisteredError.

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const refreshUser = useCallback(async () => {
    const current = await auth.currentUser();
    setUser(current);
    return current;
  }, []);

  useEffect(() => {
    let active = true;

    // onAuthStateChange fires immediately with the restored session, so this
    // covers the initial load as well as later sign-in/sign-out.
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;

      if (!session?.user) {
        setUser(null);
        setIsLoadingAuth(false);
        return;
      }

      // The callback runs inside the SDK's own lock; awaiting a Supabase call
      // directly here can deadlock, so the profile fetch is deferred a tick.
      setTimeout(async () => {
        if (!active) return;
        try {
          await refreshUser();
        } catch {
          setUser(null);
        } finally {
          if (active) setIsLoadingAuth(false);
        }
      }, 0);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [refreshUser]);

  const logout = useCallback(async (redirectTo = '/') => {
    setUser(null);
    await auth.logout(redirectTo);
  }, []);

  const navigateToLogin = useCallback(() => {
    auth.redirectToLogin(window.location.pathname);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        isLoadingAuth,
        refreshUser,
        logout,
        navigateToLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
